import { supabase } from './supabase';
import type { Profile, ProfessorInteraction, Email, Portfolio } from './types';

// Rate limiting configuration
const RATE_LIMITS = {
  FREE: {
    requests: 100,
    interval: 60 * 60 * 1000, // 1 hour
  },
  BASIC: {
    requests: 1000,
    interval: 60 * 60 * 1000, // 1 hour
  },
  PREMIUM: {
    requests: 5000,
    interval: 60 * 60 * 1000, // 1 hour
  }
};

let requestCount = 0;
let lastReset = Date.now();

async function checkRateLimit() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get user's subscription tier
  // const { data: subscription } = await supabase
  //   .from('subscriptions')
  //   .select('price_id')
  //   .eq('user_id', user.id)
  //   .eq('status', 'active')
  //   .single();

  // const tier = subscription?.price_id || 'FREE';
  const limit = RATE_LIMITS.PREMIUM;

  // Reset counter if interval has passed
  if (Date.now() - lastReset > limit.interval) {
    requestCount = 0;
    lastReset = Date.now();
  }

  // Check if limit exceeded
  if (requestCount >= limit.requests) {
    throw new Error(`Rate limit exceeded. Please try again in ${Math.ceil((limit.interval - (Date.now() - lastReset)) / 1000 / 60)} minutes.`);
  }

  requestCount++;
}

// Wrap API calls with rate limiting
async function withRateLimit<T>(fn: () => Promise<T>): Promise<T> {
  await checkRateLimit();
  return fn();
}

// Export rate-limited API functions
export const getProfile = () => withRateLimit(async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', session.user.id);

  if (error) throw error;


  return data;
});

export const updateProfile = (profile: Partial<Profile>) => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .update({
      ...profile,
      updated_at: new Date().toISOString()
    })
    .eq('id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
});

// export const createProfile = async (userId: String) => {
//   const { data: { user } } = await supabase.auth.getUser();
//   if (!user) throw new Error('Not authenticated');

//   const { data, error } = await supabase
//     .from('profiles')
//     .insert({ id: userId })
//     .select();

//   if (error) throw error;
//   return data;
// }

export const getProfessorInteractions = () => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('professor_interactions')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
});

export const createProfessorInteraction = (interaction: Omit<ProfessorInteraction, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('professor_interactions')
    .insert({
      ...interaction,
      user_id: user.id
    })
    .select()
    .single();

  if (error) throw error;
  return data.id;
});

export const updateProfessorInteraction = (id: string, updates: Partial<ProfessorInteraction>) => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('professor_interactions')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
});

export const getEmails = () => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('emails')
    .select(`
      *,
      professor_interactions (
        professor_name,
        university,
        email
      )
    `)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
});

export const createEmail = (email: Omit<Email, 'id' | 'user_id' | 'created_at'>) => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('emails')
    .insert({
      ...email,
      user_id: user.id
    })
    .select()
    .single();

  if (error) throw error;
  return data;
});

export const getPortfolio = () => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile } = await supabase
    .from('profiles')
    .select('portfolio_id')
    .eq('id', user.id)
    .single();

  if (!profile?.portfolio_id) return null;

  const { data, error } = await supabase
    .from('portfolios')
    .select(`
      *,
      research_projects (*),
      publications (*),
      awards (*),
      certifications (*),
      skills (*)
    `)
    .eq('id', profile.portfolio_id)
    .single();

  if (error) throw error;
  return data;
});

export const getProfessorReplies = (interactionId: string) => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('email_replies')
    .select('*')
    .eq('user_id', user.id)
    .eq('interaction_id', interactionId)
    .order('received_at', { ascending: false });

  if (error) throw error;
  return data;
});

export const markReplyAsRead = (replyId: string) => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('email_replies')
    .update({ read: true })
    .eq('id', replyId)
    .eq('user_id', user.id);

  if (error) throw error;
  return true;
});

export const getUnreadReplyCount = () => withRateLimit(async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { count, error } = await supabase
    .from('email_replies')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .eq('read', false);

  if (error) throw error;
  return count || 0;
});